/* eslint-disable no-underscore-dangle */
import React, { FC } from 'react';
import { Card, Badge } from 'antd';
import Button from 'components/generalUI/Button';
import FeatureIcon from 'components/generalUI/FeatureIcon';
import {
  Pack,
  EntranceTicketType,
  EntranceImage,
  ActivityImage,
  PackImage,
  WithFeatures,
} from 'apollo/types';
import priceFormat from 'utils/formats/priceFormat';
import placeholderImage from 'assets/images/placeholder-image.png';
import BadgeIcon from 'assets/images/badge-icon.svg';
import { t } from 'i18next';
import getTicketDisplayName from 'utils/helpers/getTicketDisplayName';
import { getOfferedFeatures } from 'utils/helpers/getIncludedFeatures';
import getFeatureIconVariant from 'utils/helpers/getFeatureIconVariant';
import getFeatureLabel from 'utils/helpers/getFeatureLabel';
import captureEvent from 'utils/post-hog';
import * as events from 'utils/post-hog/events';
import styles from './EntranceCard.module.scss';

interface Props {
  item: WithFeatures<Pack> | WithFeatures<EntranceTicketType>;
  isPack: boolean;
  images: EntranceImage[] | ActivityImage[] | PackImage[];
}

const EntranceCard: FC<Props> = ({ item, isPack, images }: Props) => {
  const { Meta, Grid } = Card;
  const firstImage = images.length ? images[0] : null;

  const features = getOfferedFeatures(item.features);

  const isFullPack = () => {
    const fullPackIncludedElements = ['entrada general', 'aviario', 'jardín botánico'];
    return (
      isPack &&
      fullPackIncludedElements.every((name) => item.name?.toLowerCase().includes(name))
    );
  };

  const url = isPack
    ? `/ticketSelection/packs/${item.id}`
    : `/ticketSelection/entrances/${item.id}`;

  const placeholder = (
    <img className={styles.CardImage} src={placeholderImage} alt="placeholder" />
  );
  const title = <h3>{getTicketDisplayName(item.name)}</h3>;

  const isClosed =
    item.__typename === 'EntranceTicketType' && !item.status && item.visibility;

  const coverClass = `${styles.CountBadge} ${isClosed ? styles.LoweredCover : ''}`;

  const closedBanner = isClosed ? (
    <div className={styles.ClosedBanner}>
      {t('pages.ticketSelection.closedTemporary')}
    </div>
  ) : null;

  return (
    <div className={styles.Container}>
      <Card
        className={styles.Card}
        bordered={false}
        cover={
          <>
            {closedBanner}
            {isPack ? (
              <Badge className={coverClass}>
                {isFullPack() && (
                  <img src={BadgeIcon} alt="badge" className={styles.FullIcon} />
                )}
                {firstImage ? (
                  <img
                    className={styles.PackCardImage}
                    alt="Pack"
                    src={firstImage?.image || ''}
                  />
                ) : (
                  placeholder
                )}
              </Badge>
            ) : (
              <Badge className={coverClass} color="#f26d31">
                {firstImage ? (
                  <img
                    className={styles.CardImage}
                    alt="Entrance"
                    src={firstImage?.image || ''}
                  />
                ) : (
                  placeholder
                )}
              </Badge>
            )}
          </>
        }
      >
        <Meta className={styles.CardText} title={title} />

        {features.length > 0 && (
          <Grid className={styles.IncludesSpace} hoverable={false}>
            <p className={styles.IncludesTitle}>
              {t('pages.ticketSelection.whatIncludes')}
            </p>
            <div className={styles.IncludesIcons}>
              {features.map((feature, index) => (
                <FeatureIcon
                  key={feature.slug}
                  icon={feature.slug}
                  variant={getFeatureIconVariant(feature, index)}
                  description={getFeatureLabel(feature, t)}
                />
              ))}
            </div>
          </Grid>
        )}

        <Grid className={styles.PricesSpace} hoverable={false}>
          <div className={styles.PricesHeader}>
            <p className={styles.PricesTitle}>{t('pages.ticketSelection.prices')}</p>
            <p className={styles.PricesDisclaimer}>
              {t('pages.ticketSelection.childrenEnterForFree')}
            </p>
          </div>
          <div className={styles.PriceBoxes}>
            <div className={`${styles.PriceBox} ${styles.PriceBoxWide}`}>
              <p className={styles.PriceBoxTitle}>{t('pages.ticketSelection.others')}</p>
              <p className={styles.PriceBoxAges}>
                {t('pages.ticketSelection.othersAges')}
              </p>
              <p className={styles.PriceBoxValue}>
                {priceFormat.format(item.otherValue || 0)}
              </p>
            </div>
            <div className={styles.PriceBox}>
              <p className={styles.PriceBoxTitle}>{t('pages.ticketSelection.adults')}</p>
              <p className={styles.PriceBoxAges}>
                {t('pages.ticketSelection.adultsAges')}
              </p>
              <p className={styles.PriceBoxValue}>
                {priceFormat.format(item.adultValue || 0)}
              </p>
            </div>
          </div>
        </Grid>
        <Grid className={styles.SelectSpace} hoverable={false}>
          <Button
            href={isClosed ? undefined : url}
            className={styles.SelectButton}
            key={item.name}
            kind="secondary"
            disabled={isClosed}
            onClick={() =>
              captureEvent(events.toSelectEntrancesButtonClicked, {
                flow: events.flows.purchase,
                item_type: isPack ? 'pack' : 'entrance',
                item_id: item.id,
                item_name: getTicketDisplayName(item.name),
              })
            }
          >
            {t('pages.ticketSelection.select')}
          </Button>
        </Grid>
      </Card>
    </div>
  );
};

export default EntranceCard;
